import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useOnboardingStore } from '../../store/onboardingStore';
import { authAPI } from '../../services/api';
import { ThemeToggle } from '../ui';
import { OnboardingModal } from '../onboarding';

type ViewMode = 'agent' | 'team';

export default function Layout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('agent');
  const { completed: onboardingCompleted, open: openOnboarding } = useOnboardingStore();

  // Trigger onboarding for new users
  useEffect(() => {
    if (!onboardingCompleted) {
      openOnboarding();
    }
  }, [onboardingCompleted, openOnboarding]);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      logout();
      navigate('/login');
    }
  };

  // All nav links with mode visibility - keeps layout stable when switching views
  const navLinks: Array<{ to: string; label: string; icon: string; modes: ViewMode[] }> = [
    { to: '/dashboard', label: 'Dashboard', icon: '🏠', modes: ['agent', 'team'] },
    { to: '/contacts', label: 'Contacts', icon: '👥', modes: ['agent', 'team'] },
    { to: '/tags', label: 'Tags', icon: '🏷️', modes: ['agent', 'team'] },
    { to: '/team-dashboard', label: 'Team', icon: '📊', modes: ['team'] },
    { to: '/campaigns', label: 'Campaigns', icon: '🏆', modes: ['team'] },
  ];

  // Filter links for mobile nav (actually removes items)
  const mobileNavLinks = navLinks.filter(link => link.modes.includes(viewMode));

  return (
    <div className="relative min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] transition-colors overflow-hidden">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-28 -left-24 w-96 h-96 bg-gradient-to-br from-ondeck-200/60 via-white/60 to-transparent blur-3xl" />
        <div className="absolute -top-10 right-[-6rem] w-[420px] h-[420px] bg-gradient-to-bl from-baseball-red/20 via-ondeck-200/25 to-transparent blur-[120px]" />
        <div className="absolute bottom-[-18rem] left-1/2 -translate-x-1/2 w-[80%] max-w-5xl h-[360px] bg-gradient-to-r from-baseball-brown/10 via-ondeck-300/20 to-transparent blur-[140px]" />
      </div>
      {/* Top Header */}
      <header className="sticky top-0 z-40 relative backdrop-blur-md bg-white/90 dark:bg-gray-900/80 border-b border-[var(--border-color)] dark:border-gray-800 shadow-[0_14px_48px_-32px_rgba(0,0,0,0.6)]">
        <div
          className="absolute inset-x-0 -top-[1px] h-1 bg-gradient-to-r from-baseball-red via-ondeck-500 to-baseball-brown/80 opacity-80 pointer-events-none"
          aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex items-center justify-between h-16">
            {/* Logo + User Name + View Toggle */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-2xl">⚾</span>
                <div>
                  <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                    OnDeck
                  </h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
                    {user?.name || 'Agent'}
                  </p>
                </div>
              </div>

              {/* View Mode Toggle */}
              <div className="hidden sm:flex items-center bg-white/70 dark:bg-gray-800/70 border border-[var(--border-color)] dark:border-gray-700 rounded-xl p-1 shadow-[0_10px_40px_-32px_rgba(0,0,0,0.45)] backdrop-blur">
                <button
                  onClick={() => {
                    setViewMode('agent');
                    navigate('/dashboard');
                  }}
                  title="Agent View"
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                    viewMode === 'agent'
                      ? 'bg-gradient-to-r from-ondeck-500 to-ondeck-600 text-white shadow-md shadow-ondeck-600/30'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-white/70 dark:hover:bg-gray-700/70'
                  }`}
                >
                  <span>🧑</span>
                  <span className="hidden lg:inline">Agent</span>
                </button>
                <button
                  onClick={() => {
                    setViewMode('team');
                    navigate('/team-dashboard');
                  }}
                  title="Team View"
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                    viewMode === 'team'
                      ? 'bg-gradient-to-r from-purple-500 to-ondeck-600 text-white shadow-md shadow-purple-900/20'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-white/70 dark:hover:bg-gray-700/70'
                  }`}
                >
                  <span>👥</span>
                  <span className="hidden lg:inline">Team</span>
                </button>
              </div>
            </div>

            {/* Navigation - always render all items, use visibility to hide/show */}
            <nav className="hidden md:flex items-center gap-1.5 lg:gap-2 justify-center px-1">
              {navLinks.map((link) => {
                const isVisible = link.modes.includes(viewMode);
                return (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    title={link.label}
                    className={({ isActive }) =>
                      `px-3 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 ${
                        !isVisible
                          ? 'invisible pointer-events-none'
                          : isActive
                            ? 'bg-gradient-to-r from-ondeck-500 to-ondeck-600 text-white shadow-md shadow-ondeck-600/30 ring-1 ring-ondeck-500/40'
                            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-white/70 dark:hover:bg-gray-800/70 border border-transparent hover:border-ondeck-100 dark:hover:border-gray-700'
                      }`
                    }
                    tabIndex={isVisible ? 0 : -1}
                  >
                    <span className="lg:mr-1.5">{link.icon}</span>
                    <span className="hidden lg:inline">{link.label}</span>
                  </NavLink>
                );
              })}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-1">
              <ThemeToggle />
              <NavLink
                to="/settings"
                title="Settings"
                className={({ isActive }) =>
                  `hidden md:flex p-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-ondeck-50 dark:bg-ondeck-900/50 text-ondeck-700 dark:text-ondeck-400'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`
                }
              >
                <span className="text-lg">⚙️</span>
              </NavLink>
              <div className="relative group">
                <button className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-ondeck-100 dark:bg-ondeck-900 text-ondeck-600 dark:text-ondeck-400 flex items-center justify-center font-medium">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                  <svg className="w-4 h-4 text-gray-500 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown */}
                <div className="absolute right-0 mt-2 w-56 bg-white/95 dark:bg-gray-800/95 backdrop-blur rounded-xl shadow-2xl border border-[var(--border-color)] dark:border-gray-700 py-1 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-150">
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                    <p className="font-medium text-gray-900 dark:text-white">{user?.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        viewMode === 'agent'
                          ? 'bg-ondeck-100 dark:bg-ondeck-900/50 text-ondeck-700 dark:text-ondeck-400'
                          : 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-400'
                      }`}>
                        {viewMode === 'agent' ? 'Agent View' : 'Team View'}
                      </span>
                    </div>
                  </div>

                  {/* Mobile View Toggle */}
                  <div className="sm:hidden px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Switch View</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setViewMode('agent');
                          navigate('/dashboard');
                        }}
                        className={`flex-1 px-2 py-1 text-xs rounded flex items-center justify-center gap-1 ${
                          viewMode === 'agent'
                            ? 'bg-ondeck-100 dark:bg-ondeck-900 text-ondeck-700 dark:text-ondeck-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        <span>🧑</span> Agent
                      </button>
                      <button
                        onClick={() => {
                          setViewMode('team');
                          navigate('/team-dashboard');
                        }}
                        className={`flex-1 px-2 py-1 text-xs rounded flex items-center justify-center gap-1 ${
                          viewMode === 'team'
                            ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        <span>👥</span> Team
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/settings')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Settings
                  </button>
                  <button
                    onClick={() => navigate('/help')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Help & Support
                  </button>
                  <hr className="my-1 border-gray-200 dark:border-gray-700" />
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/90 dark:bg-gray-900/85 backdrop-blur-md border-t border-[var(--border-color)] dark:border-gray-800 z-40 pb-safe shadow-[0_-12px_40px_-28px_rgba(0,0,0,0.65)]">
        <div className="flex">
          {mobileNavLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center gap-1 py-3 text-xs min-h-[52px] transition-colors ${
                  isActive
                    ? 'text-ondeck-600 dark:text-ondeck-300 font-semibold'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`
              }
            >
              <span className="text-xl mb-1">{link.icon}</span>
              {link.label}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="pb-20 md:pb-0">
        <Outlet />
      </main>

      {/* Onboarding Modal */}
      <OnboardingModal />
    </div>
  );
}
